<template>
 <site-shell auth-page>
  <div class="auth-page bs-foundation">
   <div class="auth-column">
    <h1 class="auth-heading">{{ external ? 'Complete your account' : reset ? 'Choose a new password' : 'Reset your password' }}</h1>
    <p class="auth-lede">{{ external ? 'Pick a username to finish signing up.' : reset ? 'Enter a new password for your account.' : "Enter your account's email address and we'll send you a reset link." }}</p>

    <div class="auth-card bs:bg-surface bs:rounded-sm bs:shadow-card">
     <p v-if="loading" class="auth-status" role="status">Preparing form…</p>
     <div v-if="loadError" class="auth-notice auth-notice-error" role="alert">
      <p>{{ loadError }} <button type="button" class="bs-button" @click="load">Try again</button></p>
     </div>
     <div v-if="form.saved && !reset && !external" class="auth-notice auth-notice-success" role="status">
      <p>If an account exists for that address, a reset link is on its way. Check your email.</p>
     </div>
     <div v-if="summary.length" ref="summary" class="auth-notice auth-notice-error" role="alert" tabindex="-1">
      <p v-for="(line, index) in summary" :key="'summary-' + index">{{ line }}</p>
     </div>

     <form class="auth-form" novalidate @submit.prevent="submit">
      <div v-for="field in fields" :key="field.name" class="bs-field">
       <label :for="field.id">{{ field.label }}</label>
       <div :class="field.type === 'password' ? 'auth-password' : 'auth-input'">
        <input class="bs-input" :id="field.id" v-model="draft[field.name]" :type="field.type === 'password' && showPassword ? 'text' : field.type"
         :autocomplete="field.autocomplete" :minlength="field.minlength" :maxlength="field.maxlength"
         :autocapitalize="field.type === 'password' ? null : 'none'" :spellcheck="field.type === 'password' ? null : 'false'"
         required :aria-invalid="inlineErrors(field.name).length ? 'true' : 'false'" :aria-describedby="describedBy(field)">
        <button v-if="field.type === 'password'" type="button" class="bs-button auth-reveal"
         :aria-controls="field.id" aria-label="Show password" :aria-pressed="showPassword ? 'true' : 'false'"
         @click="showPassword = !showPassword">{{ showPassword ? 'Hide' : 'Show' }}</button>
       </div>
       <p v-if="field.hint" :id="field.id + '-hint'" class="auth-hint">{{ field.hint }}</p>
       <p v-for="(error, index) in inlineErrors(field.name)" :key="field.name + '-error-' + index"
        :id="index === 0 ? field.id + '-error' : null" class="auth-field-error">{{ error }}</p>
      </div>
      <AppButton type="submit" variant="primary" class="auth-wide" :loading="form.pending" :disabled="loading || !!loadError">{{ form.pending ? 'Sending…' : external ? 'Complete account' : reset ? 'Save new password' : 'Send reset link' }}</AppButton>
     </form>
     <p v-if="external" class="auth-alt">Wrong account? Start again with <a href="/external-login?provider=google">Google</a> or <a href="/external-login?provider=facebook">Facebook</a>.</p>
     <p v-if="reset" class="auth-alt">Link not working? <a href="/account/resetpwd">Request a new reset link</a>.</p>
     <p v-if="!external" class="auth-switch">Remembered it? <a href="/login">Log in</a></p>
    </div>
   </div>
  </div>
 </site-shell>
</template>
<script>
import axios from 'axios';
import SiteShell from './SiteShell.vue';
import AppButton from '../components/ui/AppButton.vue';
import createForm from './form';
const NETWORK_FAILURE = 'Unable to save. Please try again.';
const FIELD_SUMMARY = 'Please correct the fields below.';
export default {
 components: { SiteShell, AppButton },
 data() {
  return {
   external: /^\/external-login\/create\/?$/i.test(location.pathname),
   reset: /^\/account\/resetpwd\/[^/]+\/?$/i.test(location.pathname),
   draft: { Username: '', Email: '', Password: '' }, token: '', loading: true, loadError: '', showPassword: false, localErrors: {},
   form: createForm(async draft => {
    const body = new URLSearchParams();
    Object.keys(draft).forEach(k => body.append(k, draft[k]));
    body.append('__RequestVerificationToken', this.token);
    const endpoint = this.external ? 'external' : this.reset ? 'reset/' + location.pathname.split('/')[3] : 'recovery';
    const result = await axios.post('/application/auth/' + endpoint, body);
    if (result.data.redirect) { draft.Password = ''; location.assign(result.data.redirect); }
   })
  };
 },
 computed: {
  fields() {
   const fields = [];
   if (this.external) fields.push({ name: 'Username', id: 'username', label: 'Username', type: 'text', autocomplete: 'username', minlength: 3, maxlength: 50, hint: '3 to 50 letters, numbers, dots, dashes or underscores.' });
   if (!this.reset) fields.push({ name: 'Email', id: 'email', label: 'Email address', type: 'email', autocomplete: 'email' });
   if (this.reset) fields.push({ name: 'Password', id: 'password', label: 'New password', type: 'password', autocomplete: 'new-password', minlength: 5, maxlength: 100, hint: 'At least 5 characters.' });
   return fields;
  },
  summary() {
   const names = this.fields.map(f => f.name);
   const lines = [];
   Object.keys(this.form.errors).forEach(key => { if (names.indexOf(key) < 0) this.form.errors[key].forEach(e => lines.push(e)); });
   const message = this.form.message;
   if (message === NETWORK_FAILURE) lines.push("Couldn't reach Brew Status. Check your connection and try again.");
   else if (message && message !== FIELD_SUMMARY) lines.push(message);
   return lines;
  }
 },
 methods: {
  inlineErrors(name) { return this.localErrors[name] ? [this.localErrors[name]] : (this.form.errors[name] || []); },
  describedBy(field) {
   const ids = [];
   if (field.hint) ids.push(field.id + '-hint');
   if (this.inlineErrors(field.name).length) ids.push(field.id + '-error');
   return ids.length ? ids.join(' ') : null;
  },
  async load() {
   this.loading = true; this.loadError = '';
   try {
    this.token = (await axios.get('/application/auth/token')).data.token;
    if (this.external) Object.assign(this.draft, (await axios.get('/application/auth/external')).data);
   } catch (e) {
    this.loadError = this.external ? 'Unable to load your Google or Facebook details. Start again or retry.' : 'Unable to prepare the form.';
   } finally { this.loading = false; }
  },
  async submit() {
   const missing = {};
   this.fields.forEach(f => { if (!String(this.draft[f.name] || '').trim()) missing[f.name] = 'Enter your ' + f.label.toLowerCase() + '.'; });
   this.localErrors = missing;
   const first = this.fields.filter(f => missing[f.name])[0];
   if (first) { this.form.errors = {}; this.form.message = ''; document.getElementById(first.id).focus(); return; }
   await this.form.submit(this.draft);
   await this.$nextTick();
   if (this.summary.length && this.$refs.summary) this.$refs.summary.focus();
   else {
    const invalid = this.fields.filter(f => this.inlineErrors(f.name).length)[0];
    if (invalid) document.getElementById(invalid.id).focus();
   }
  }
 },
 created() {
  document.title = (this.external ? 'Complete your account' : this.reset ? 'Choose a new password' : 'Reset your password') + ' · Brew Status';
  this.load();
 }
};
</script>
